import React from 'react';
import {
  Row,
  Col,
  Select,
} from 'antd';

import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';
import OnTraccrNumberInput from '../../../common/inputs/OnTraccrNumberInput';
import HoverHelp from '../../../common/HoverHelp';

import FieldTriggerCheckbox from './FieldTriggerCheckbox';
import OptionalRow from './OptionalRow';

import ConditionalRenderingRow from './ConditionalRenderingRow';
import GPSLocationFormFieldPreview from './GPSLocationFormFieldPreview';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';
import HideIfEmptyInPDFCheckbox from './HideIfEmptyInPDFCheckbox';

const configure = ({
  setConfigProps,
  configProps = {},
  isExternalForm,
  sections = [],
  templateId,
  divisions,
  projectId,
  name,
  isBoardCards,
  setFieldTriggerEditable,
  id,
  customers = [],
  projects = [],
  users = [],
  costcodes = [],
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  formTemplates = [],
  labels = [],
  contactAddressBooks = {},
  bucketDropdownsWithGPSField = [],
  bucketTemplatesWithGPSFieldMap = {},
  buckets = [],
  scheduleCategories = [],
  isV3Form = false,
} = {}) => {
  const setTitle = (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    const newConfig = {
      ...configProps,
      numAnswers: configProps?.numAnswers ?? 1,
      title: value,
    };
    setConfigProps(newConfig);
  };

  const onNumberChange = (num) => {
    const newConfig = {
      ...configProps,
      numAnswers: num,
      useCoordinates: false,
    };
    setConfigProps(newConfig);
  };

  const onCheckChanged = (key) => (checked) => {
    const updateObj = {
      ...configProps,
      [key]: checked,
    };
    if (key === 'openLimit' && checked) {
      updateObj.useCoordinates = false;
    }
    setConfigProps(updateObj);
  };

  const setFieldTrigger = (checked) => {
    setConfigProps({
      ...configProps,
      fieldTrigger: checked,
    });
  };

  const setConditionalRenderingFormula = (formula) => {
    setConfigProps({
      ...configProps,
      conditionalRenderingFormula: formula,
    });
  };

  const setHasConditionalRendering = (checked) => {
    setConfigProps({
      ...configProps,
      hasConditionalRendering: checked,
    });
  };

  const setBucketField = (newValue) => setConfigProps({
    ...configProps,
    bucketField: newValue,
  });

  const setBucketGPSLocationField = (newValue) => setConfigProps({
    ...configProps,
    bucketGPSLocationField: newValue,
  });

  const {
    optional,
    openLimit,
    numAnswers = 1,
    hasConditionalRendering = false,
    conditionalRenderingFormula,
    bucketField,
    bucketGPSLocationField,
    useCoordinates = false,
  } = configProps;

  let bucketGPSLocationFieldOptions = [];

  if (bucketField) {
    const relevantBucket = bucketDropdownsWithGPSField?.find(
      (bucket) => bucket.value === bucketField
    );
    const bucketTemplateId = relevantBucket?.configProps?.subDataType;
    const bucketTemplateFields = bucketTemplatesWithGPSFieldMap[bucketTemplateId]?.customFields?.fields;
    bucketGPSLocationFieldOptions = bucketTemplateFields
      ?.filter((field) => field.selectedType === 'gpsLocation')
      ?.map((field) => ({
        label: field.title,
        value: field.id,
      }));
  }

  return (
    <div>
      <Row className="form-required-field">
        Title:
      </Row>
      <Row style={{ marginTop: 5, width: 350 }}>
        <OnTraccrTextInput
          textarea
          placeholder="Insert title here"
          onChange={setTitle}
          value={configProps.title}
        />
      </Row>
      <OptionalRow onChange={onCheckChanged('optional')} optional={optional} />
      <ConditionalRenderingRow
        onChange={setHasConditionalRendering}
        onFormulaChange={setConditionalRenderingFormula}
        hasConditionalRendering={hasConditionalRendering}
        conditionalRenderingFormula={conditionalRenderingFormula}
        id={id}
        sections={sections}
        customers={customers}
        projects={projects}
        users={users}
        costcodes={costcodes}
        phases={phases}
        projectIdMap={projectIdMap}
        vendors={vendors}
        equipment={equipment}
        formTemplates={formTemplates}
        labels={labels}
        contactAddressBooks={contactAddressBooks}
        buckets={buckets}
        scheduleCategories={scheduleCategories}
      />
      <HideIfEmptyInPDFCheckbox
        isV3Form={isV3Form}
        configProps={configProps}
        setConfigProps={setConfigProps}
      />
      <OnTraccrCheckbox
        style={{ marginTop: 10 }}
        value={useCoordinates}
        onChange={onCheckChanged('useCoordinates')}
        disabled={numAnswers !== 1 || openLimit}
        label="Use Coordinates"
        hoverNode={(
          <div style={{ width: 300 }}>
            Check this box if you want to configure the GPS
            {' '}
            location using coordinates instead of an address
          </div>
        )}
      />
      {!!bucketDropdownsWithGPSField?.length && (
        <>
          <Row style={{ marginTop: 15 }} gutter={10}>
            <Col>
              Select which bucket to map into GPS location:
            </Col>
            <Col>
              <HoverHelp placement="left" content="Select which bucket to map into this field. Only buckets with a GPS location field can be mapped." />
            </Col>
          </Row>
          <Row>
            <Select
              style={{ width: 350 }}
              onChange={setBucketField}
              allowClear
              options={bucketDropdownsWithGPSField}
              value={bucketField}
            />
          </Row>
        </>
      )}
      {bucketField && (
        <>
          <Row style={{ marginTop: 15 }} gutter={10}>
            <Col>
              Select bucket GPS location custom field to map into this field:
            </Col>
            <Col>
              <HoverHelp placement="left" content="Select which bucket GPS location custom field to map into this field." />
            </Col>
          </Row>
          <Row>
            <Select
              style={{ width: 350 }}
              onChange={setBucketGPSLocationField}
              allowClear
              options={bucketGPSLocationFieldOptions}
              value={bucketGPSLocationField}
            />
          </Row>
        </>
      )}
      <Row style={{ marginTop: 15 }} gutter={10}>
        <Col>
          Number of answers:
        </Col>
        <Col>
          <HoverHelp placement="top" content="Configure how many options the user can select" />
        </Col>
      </Row>
      <Row style={{ marginTop: 5 }} align="middle" gutter={20}>
        <Col style={{ height: 32 }}>
          <OnTraccrNumberInput
            style={{ width: 100 }}
            defaultValue={1}
            value={numAnswers}
            min={1}
            onChange={onNumberChange}
            disabled={openLimit}
          />
        </Col>
        <Col style={{ height: 32 }}>
          <OnTraccrCheckbox
            style={{ alignItems: 'center', height: '100%' }}
            value={openLimit}
            onChange={onCheckChanged('openLimit')}
            label="Leave open?"
            hoverNode="Check this box if you want to remove restrictions on the number of answers"
            hoverIconProps={{ style: { marginTop: 0 }}}
          />
        </Col>
      </Row>
      {!isBoardCards && !isExternalForm && (
        <FieldTriggerCheckbox
          onChange={setFieldTrigger}
          onEditableChange={setFieldTriggerEditable}
          sections={sections}
          projectId={projectId}
          templateId={templateId}
          divisions={divisions}
          configProps={configProps}
          setConfigProps={setConfigProps}
          name={name}
        />
      )}
    </div>
  );
};

export default {
  configure,
  preview: (props = {}) => (<GPSLocationFormFieldPreview {...props} />),
  title: 'GPS Location',
  description: 'User can select one or more GPS locations on a map',
};
